import * as THREE from 'three';
import type { GameState } from '@shared/game/types';
import type { GsapRegistry, GsapTimelineHandle } from '../vfx/gsap-registry';
import type { CharacterRig } from './types';
import { getRigRenderMeshes } from './mesh-targets';
import { getItemVfxSmokeTexture, ITEM_VFX_SMOKE_ALPHA_TEST } from '../vfx/items/sprite-textures';
import { createStoneSkinShader } from '../shaders/stone-skin';

const STATUS_SHELL_RENDER_ORDER = 20;
const ICE_SHELL_SCALE = 1.2;
const STONE_SHELL_SCALE = 1.2;

export type CharacterStatusEffectId = 'ice' | 'stone' | 'curse';

export interface CharacterStatusEffectManager {
  show(playerId: string, effectId: CharacterStatusEffectId): void;
  hide(playerId: string, effectId: CharacterStatusEffectId, animate?: boolean): void;
  refresh(playerId: string): void;
  cleanupPlayer(playerId: string): void;
  syncFromState(state: GameState): void;
  dispose(): void;
}

interface CharacterStatusEffectManagerConfig {
  readonly registry: GsapRegistry;
  readonly getRig: (playerId: string) => CharacterRig | null;
}

interface EffectRecord {
  readonly id: CharacterStatusEffectId;
  readonly playerId: string;
  readonly rig: CharacterRig;
  readonly group: THREE.Group;
  readonly handles: Set<GsapTimelineHandle>;
  finishing: boolean;
}

export function createCharacterStatusEffectManager(
  config: CharacterStatusEffectManagerConfig,
): CharacterStatusEffectManager {
  const byPlayer = new Map<string, Map<CharacterStatusEffectId, EffectRecord>>();
  const shellSuppressedVisibilityByPlayer = new Map<string, Map<THREE.Object3D, boolean>>();
  let disposed = false;

  function show(playerId: string, effectId: CharacterStatusEffectId): void {
    if (disposed) return;
    const rig = config.getRig(playerId);
    if (rig == null) return;

    const existing = byPlayer.get(playerId)?.get(effectId);
    if (existing != null) {
      if (existing.rig === rig && existing.group.parent === rig.root && !existing.finishing) {
        syncShellSuppression(playerId, rig);
        return;
      }
      destroyRecord(existing);
    }

    const record =
      effectId === 'curse'
        ? buildCurseRecord(playerId, rig, config.registry)
        : buildShellRecord(playerId, effectId, rig, config.registry);
    rig.root.add(record.group);
    let playerRecords = byPlayer.get(playerId);
    if (playerRecords == null) {
      playerRecords = new Map();
      byPlayer.set(playerId, playerRecords);
    }
    playerRecords.set(effectId, record);
    syncShellSuppression(playerId, rig);
  }

  function hide(playerId: string, effectId: CharacterStatusEffectId, animate = true): void {
    const record = byPlayer.get(playerId)?.get(effectId);
    if (record == null) return;
    if (!animate) {
      removeRecord(record);
      return;
    }
    playExit(record);
  }

  function refresh(playerId: string): void {
    const records = byPlayer.get(playerId);
    if (records == null || records.size === 0) return;
    const activeIds = [...records.keys()];
    cleanupPlayer(playerId);
    for (const effectId of activeIds) show(playerId, effectId);
  }

  function cleanupPlayer(playerId: string): void {
    const records = byPlayer.get(playerId);
    if (records != null) {
      for (const record of records.values()) destroyRecord(record);
      byPlayer.delete(playerId);
    }
    restoreShellSuppressedObjects(playerId);
  }

  function syncFromState(state: GameState): void {
    const activeByPlayer = new Map<string, Set<CharacterStatusEffectId>>();
    const activelyCursedPlayers = new Set(
      state.activeCursedRelics
        .filter((relic) => relic.turnsRemaining > 0)
        .map((relic) => relic.victimId),
    );
    for (const [playerId, player] of state.players) {
      const active = new Set<CharacterStatusEffectId>();
      if (
        player.statusEffects.frozenUntil != null &&
        player.statusEffects.frozenUntil >= state.round
      ) {
        active.add('ice');
      }
      if (player.statusEffects.stoneSkin.active) active.add('stone');
      if (player.cursedRelicTurnsRemaining > 0 && activelyCursedPlayers.has(playerId)) {
        active.add('curse');
      }
      activeByPlayer.set(playerId, active);
    }

    for (const [playerId, effects] of activeByPlayer) {
      for (const effectId of effects) show(playerId, effectId);
      const records = byPlayer.get(playerId);
      if (records == null) continue;
      for (const effectId of [...records.keys()]) {
        if (!effects.has(effectId)) hide(playerId, effectId);
      }
    }

    for (const playerId of [...byPlayer.keys()]) {
      if (!state.players.has(playerId)) cleanupPlayer(playerId);
    }
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    for (const playerId of [...byPlayer.keys()]) cleanupPlayer(playerId);
  }

  function removeRecord(record: EffectRecord): void {
    const records = byPlayer.get(record.playerId);
    if (records?.get(record.id) === record) {
      records.delete(record.id);
      if (records.size === 0) byPlayer.delete(record.playerId);
    }
    destroyRecord(record);
    syncShellSuppression(record.playerId, record.rig);
  }

  function playExit(record: EffectRecord): void {
    if (record.finishing) return;
    record.finishing = true;
    for (const handle of record.handles) handle.dispose();
    record.handles.clear();

    if (record.id === 'stone') {
      playStoneShatterExit(record, config.registry, removeRecord);
      return;
    }

    const handle = config.registry.create(`character_status_${record.id}_exit`);
    record.handles.add(handle);
    const opacity = { value: 1 };
    handle.timeline.to(
      opacity,
      {
        value: 0,
        duration: record.id === 'curse' ? 0.45 : 0.32,
        ease: 'power1.in',
        onUpdate: () => {
          setGroupOpacity(record.group, opacity.value);
          record.group.scale.setScalar(1 + (1 - opacity.value) * 0.18);
        },
      },
      0,
    );
    handle.timeline.call(
      () => {
        removeRecord(record);
      },
      undefined,
      record.id === 'curse' ? 0.45 : 0.32,
    );
  }

  return { show, hide, refresh, cleanupPlayer, syncFromState, dispose };

  function syncShellSuppression(playerId: string, rig: CharacterRig): void {
    if (hasActiveShellRecord(playerId)) {
      suppressPlayerShellObjects(playerId, rig);
      return;
    }
    restoreShellSuppressedObjects(playerId);
  }

  function hasActiveShellRecord(playerId: string): boolean {
    const records = byPlayer.get(playerId);
    return records?.has('ice') === true || records?.has('stone') === true;
  }

  function suppressPlayerShellObjects(playerId: string, rig: CharacterRig): void {
    let visibility = shellSuppressedVisibilityByPlayer.get(playerId);
    if (visibility == null) {
      visibility = new Map();
      shellSuppressedVisibilityByPlayer.set(playerId, visibility);
    }

    for (const object of collectShellSuppressionTargets(rig)) {
      if (!visibility.has(object)) visibility.set(object, object.visible);
      object.visible = false;
    }
  }

  function restoreShellSuppressedObjects(playerId: string): void {
    const visibility = shellSuppressedVisibilityByPlayer.get(playerId);
    if (visibility == null) return;
    for (const [object, wasVisible] of visibility) {
      object.visible = wasVisible;
    }
    shellSuppressedVisibilityByPlayer.delete(playerId);
  }
}

function collectShellSuppressionTargets(rig: CharacterRig): THREE.Object3D[] {
  const targets: THREE.Object3D[] = [];
  const seen = new Set<THREE.Object3D>();
  const add = (object: THREE.Object3D) => {
    if (seen.has(object)) return;
    seen.add(object);
    targets.push(object);
  };

  for (const mesh of collectStatusSourceMeshes(rig)) add(mesh);

  rig.root.traverse((child) => {
    if (
      isCharacterEyeObject(child) ||
      (child instanceof THREE.Mesh && isCharacterOutlineMesh(child))
    ) {
      add(child);
    }
  });

  return targets;
}

function isCharacterEyeObject(object: THREE.Object3D): boolean {
  return hasTaggedAncestor(object, 'characterEyes');
}

function isCharacterOutlineMesh(mesh: THREE.Mesh): boolean {
  if (!mesh.name.endsWith(':outline')) return false;
  const material = mesh.material;
  const materials = Array.isArray(material) ? material : [material];
  return materials.some((mat) => {
    const uniforms = (mat as THREE.ShaderMaterial).uniforms;
    return uniforms?.uColor != null && uniforms.uThickness != null;
  });
}

function playStoneShatterExit(
  record: EffectRecord,
  registry: GsapRegistry,
  removeRecord: (record: EffectRecord) => void,
): void {
  const handle = registry.create('character_status_stone_shatter');
  record.handles.add(handle);

  const opacity = { value: 1 };
  const exitDuration = 0.92;
  const fadeDelay = 0.18;
  const fadeDuration = 0.68;
  const fragments: THREE.Object3D[] = [];
  record.group.traverse((child) => {
    if (child.userData.characterStatusFragment === 'stone') fragments.push(child);
  });

  addStoneDustBurst(record.group, handle);

  handle.timeline.to(
    opacity,
    {
      value: 0,
      duration: fadeDuration,
      ease: 'power1.in',
      onUpdate: () => {
        setGroupOpacity(record.group, opacity.value);
      },
    },
    fadeDelay,
  );

  fragments.forEach((fragment, index) => {
    const start = fragment.position.clone();
    const outward = new THREE.Vector3(start.x, 0, start.z);
    if (outward.lengthSq() < 0.0001) outward.set(index % 2 === 0 ? 1 : -1, 0, 0);
    outward.normalize();
    const scatter = 0.42 + (index % 4) * 0.095;
    const fall = 1.05 + (index % 3) * 0.2;

    handle.timeline.to(
      fragment.position,
      {
        x: start.x + outward.x * scatter,
        y: start.y - fall,
        z: start.z + outward.z * scatter,
        duration: exitDuration,
        ease: 'power2.in',
      },
      0,
    );
    handle.timeline.to(
      fragment.rotation,
      {
        x: fragment.rotation.x + 1.2 + index * 0.07,
        y: fragment.rotation.y + 0.9 + index * 0.05,
        z: fragment.rotation.z + 0.6 + index * 0.04,
        duration: exitDuration,
        ease: 'power1.inOut',
      },
      0,
    );
    handle.timeline.to(
      fragment.scale,
      {
        x: fragment.scale.x * 0.42,
        y: fragment.scale.y * 0.42,
        z: fragment.scale.z * 0.42,
        duration: 0.74,
        ease: 'power1.in',
      },
      0.16,
    );
  });

  handle.timeline.to(
    record.group.scale,
    {
      x: 1.08,
      y: 1.08,
      z: 1.08,
      duration: 0.16,
      ease: 'power2.out',
    },
    0,
  );
  handle.timeline.call(
    () => {
      removeRecord(record);
    },
    undefined,
    1,
  );
}

function addStoneDustBurst(group: THREE.Group, handle: GsapTimelineHandle): void {
  const smokeTexture = getItemVfxSmokeTexture();
  for (let index = 0; index < 18; index += 1) {
    const angle = (index / 18) * Math.PI * 2;
    const ring = index % 2 === 0 ? 1 : 0.62;
    const startRadius = 0.2 + (index % 3) * 0.035;
    const endRadius = 0.52 + (index % 5) * 0.055;
    const material = new THREE.SpriteMaterial({
      map: smokeTexture,
      color: index % 3 === 0 ? 0x8a8177 : index % 3 === 1 ? 0x6f675f : 0xa39a8e,
      transparent: true,
      alphaTest: ITEM_VFX_SMOKE_ALPHA_TEST,
      opacity: 0.46,
      blending: THREE.NormalBlending,
      depthWrite: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.name = `stone-shatter-dust:${index}`;
    sprite.userData.characterStatusDust = 'stone';
    sprite.position.set(Math.cos(angle) * startRadius, 0.1, Math.sin(angle) * startRadius * 0.72);
    const baseScale = 0.18 + (index % 4) * 0.035;
    sprite.scale.set(baseScale, baseScale, 1);
    group.add(sprite);

    handle.timeline.to(
      sprite.position,
      {
        x: Math.cos(angle) * endRadius * ring,
        y: 0.22 + (index % 4) * 0.035,
        z: Math.sin(angle) * endRadius * 0.7 * ring,
        duration: 0.42,
        ease: 'power2.out',
      },
      0,
    );
    handle.timeline.to(
      sprite.scale,
      {
        x: baseScale * 2.4,
        y: baseScale * 1.65,
        duration: 0.48,
        ease: 'power2.out',
      },
      0,
    );
    handle.timeline.to(
      material,
      {
        opacity: 0,
        duration: 0.52,
        ease: 'power1.in',
      },
      0.28,
    );
  }
}

function buildShellRecord(
  playerId: string,
  effectId: Exclude<CharacterStatusEffectId, 'curse'>,
  rig: CharacterRig,
  registry: GsapRegistry,
): EffectRecord {
  const group = new THREE.Group();
  group.name = `character-status-effect:${effectId}`;
  group.userData.characterStatusEffect = effectId;
  group.scale.setScalar(0.96);

  const material = createShellMaterial(effectId);
  const sources = collectStatusSourceMeshes(rig);
  for (const source of sources) {
    const clone = createShellClone(source, material.clone());
    const shellScale = effectId === 'ice' ? ICE_SHELL_SCALE : STONE_SHELL_SCALE;
    clone.name = `${effectId}-shell:${source.name || 'mesh'}`;
    copyWorldTransformIntoParent(source, rig.root, clone);
    clone.scale.multiplyScalar(shellScale);
    if (effectId === 'stone' && source === rig.bodyMesh) {
      clone.position.y += computeStoneBodyShellYOffset(source.geometry, source.scale.y, shellScale);
    }
    clone.castShadow = false;
    clone.receiveShadow = false;
    clone.frustumCulled = false;
    clone.renderOrder = STATUS_SHELL_RENDER_ORDER;
    group.add(clone);
  }

  addShellFragments(group, effectId);

  const handles = new Set<GsapTimelineHandle>();
  const handle = registry.create(`character_status_${effectId}_enter`);
  handles.add(handle);
  const opacity = { value: 0 };
  setGroupOpacity(group, 0);
  handle.timeline.to(
    group.scale,
    {
      x: 1,
      y: 1,
      z: 1,
      duration: 0.24,
      ease: 'back.out(1.7)',
    },
    0,
  );
  handle.timeline.to(
    opacity,
    {
      value: 1,
      duration: 0.22,
      ease: 'power1.out',
      onUpdate: () => setGroupOpacity(group, opacity.value),
    },
    0,
  );

  return { id: effectId, playerId, rig, group, handles, finishing: false };
}

function collectStatusSourceMeshes(rig: CharacterRig): THREE.Mesh[] {
  const result: THREE.Mesh[] = [];
  const seen = new Set<THREE.Mesh>();
  const add = (mesh: THREE.Mesh) => {
    if (isExcludedStatusSourceMesh(mesh, rig)) return;
    if (seen.has(mesh)) return;
    seen.add(mesh);
    result.push(mesh);
  };

  for (const mesh of getRigRenderMeshes(rig)) add(mesh);

  if (rig._headRoot != null) {
    rig._headRoot.traverse((child) => {
      if (child instanceof THREE.Mesh) add(child);
    });
  } else if (rig._headMesh instanceof THREE.Mesh) {
    add(rig._headMesh);
  }

  return result;
}

function isExcludedStatusSourceMesh(mesh: THREE.Mesh, rig: CharacterRig): boolean {
  if (mesh === rig._capeMesh || isDescendantOf(mesh, rig._capeMesh)) return true;
  if (hasTaggedAncestor(mesh, 'characterEyes')) return true;
  if (hasTaggedAncestor(mesh, 'characterStatusEffect')) return true;
  return hasNamePrefix(mesh, 'dynamic-cape:');
}

function hasTaggedAncestor(object: THREE.Object3D, key: string): boolean {
  let current: THREE.Object3D | null = object;
  while (current != null) {
    if (current.userData[key] != null) return true;
    current = current.parent;
  }
  return false;
}

function hasNamePrefix(object: THREE.Object3D, prefix: string): boolean {
  let current: THREE.Object3D | null = object;
  while (current != null) {
    if (current.name.startsWith(prefix)) return true;
    current = current.parent;
  }
  return false;
}

function isDescendantOf(
  object: THREE.Object3D,
  ancestor: THREE.Object3D | null | undefined,
): boolean {
  if (ancestor == null) return false;
  let current: THREE.Object3D | null = object;
  while (current != null) {
    if (current === ancestor) return true;
    current = current.parent;
  }
  return false;
}

function createShellClone(source: THREE.Mesh, material: THREE.Material): THREE.Mesh {
  const geometry = source.geometry.clone();
  if (source instanceof THREE.SkinnedMesh) {
    const clone = new THREE.SkinnedMesh(geometry, material);
    clone.bind(source.skeleton, source.bindMatrix);
    clone.bindMatrix.copy(source.bindMatrix);
    clone.bindMatrixInverse.copy(source.bindMatrixInverse);
    return clone;
  }
  return new THREE.Mesh(geometry, material);
}

function copyWorldTransformIntoParent(
  source: THREE.Object3D,
  parent: THREE.Object3D,
  target: THREE.Object3D,
): void {
  source.updateWorldMatrix(true, false);
  parent.updateWorldMatrix(true, false);
  const localMatrix = parent.matrixWorld.clone().invert().multiply(source.matrixWorld);
  localMatrix.decompose(target.position, target.quaternion, target.scale);
}

function computeStoneBodyShellYOffset(
  geometry: THREE.BufferGeometry,
  sourceScaleY: number,
  shellScale: number,
): number {
  const position = geometry.getAttribute('position');
  if (!(position instanceof THREE.BufferAttribute) || position.count === 0) return 0;

  let maxAbsX = 0;
  for (let index = 0; index < position.count; index += 1) {
    maxAbsX = Math.max(maxAbsX, Math.abs(position.getX(index)));
  }
  if (maxAbsX === 0) return 0;

  const outerXThreshold = maxAbsX * 0.78;
  let minY = Infinity;
  let maxY = -Infinity;
  for (let index = 0; index < position.count; index += 1) {
    if (Math.abs(position.getX(index)) < outerXThreshold) continue;
    const y = position.getY(index);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }
  if (!Number.isFinite(minY) || !Number.isFinite(maxY)) return 0;

  const armBandCenterY = (minY + maxY) / 2;
  return -armBandCenterY * sourceScaleY * (shellScale - 1);
}

function buildCurseRecord(
  playerId: string,
  rig: CharacterRig,
  registry: GsapRegistry,
): EffectRecord {
  const group = new THREE.Group();
  group.name = 'character-status-effect:curse';
  group.userData.characterStatusEffect = 'curse';

  const handles = new Set<GsapTimelineHandle>();
  const smokeTexture = getItemVfxSmokeTexture();
  const hazeMaterial = new THREE.SpriteMaterial({
    map: smokeTexture,
    color: 0x2d0b4d,
    transparent: true,
    alphaTest: ITEM_VFX_SMOKE_ALPHA_TEST,
    opacity: 0,
    blending: THREE.NormalBlending,
    depthWrite: false,
  });
  const haze = new THREE.Sprite(hazeMaterial);
  haze.position.set(0, 0.72, 0);
  haze.scale.set(1.65, 2.05, 1);
  group.add(haze);

  const smokeSprites: THREE.Sprite[] = [];
  const smokeMaterials: THREE.SpriteMaterial[] = [];
  const plumeSpriteCount = 86;
  const plumeLayers = [
    { y: 0.08, width: 0.58, depth: 0.34, scale: 0.78 },
    { y: 0.24, width: 0.7, depth: 0.4, scale: 0.92 },
    { y: 0.42, width: 0.76, depth: 0.42, scale: 1.02 },
    { y: 0.6, width: 0.76, depth: 0.44, scale: 1.08 },
    { y: 0.78, width: 0.68, depth: 0.4, scale: 1.02 },
    { y: 0.98, width: 0.56, depth: 0.36, scale: 0.92 },
    { y: 1.18, width: 0.46, depth: 0.3, scale: 0.82 },
    { y: 1.36, width: 0.36, depth: 0.24, scale: 0.72 },
  ];
  const noise = (seed: number) => {
    const value = Math.sin(seed * 12.9898) * 43758.5453;
    return value - Math.floor(value);
  };

  for (let index = 0; index < plumeSpriteCount; index += 1) {
    const layer = plumeLayers[index % plumeLayers.length]!;
    const angle = noise(index * 3 + 1) * Math.PI * 2;
    const radius = 0.18 + noise(index * 5 + 2) * 0.82;
    const y = layer.y + (noise(index * 7 + 3) - 0.5) * 0.14;
    const baseX = Math.cos(angle) * layer.width * radius;
    const baseZ = Math.sin(angle) * layer.depth * radius;
    const baseScale = layer.scale * (0.92 + noise(index * 11 + 4) * 0.36);
    const material = new THREE.SpriteMaterial({
      map: smokeTexture,
      color: index % 3 === 0 ? 0x4a0d78 : index % 3 === 1 ? 0x250536 : 0x6a18a6,
      transparent: true,
      alphaTest: ITEM_VFX_SMOKE_ALPHA_TEST,
      opacity: 0,
      blending: THREE.NormalBlending,
      depthWrite: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.name = `curse-smoke-plume:${index}`;
    sprite.userData.curseSmokePlume = true;
    sprite.userData.baseY = y;
    sprite.position.set(baseX, y, baseZ);
    sprite.scale.set(baseScale * 0.85, baseScale * 0.85, 1);
    group.add(sprite);
    smokeSprites.push(sprite);
    smokeMaterials.push(material);
  }

  const enter = registry.create('character_status_curse_enter');
  handles.add(enter);
  enter.timeline.to(hazeMaterial, { opacity: 0.46, duration: 0.35, ease: 'power1.out' }, 0);

  smokeSprites.forEach((sprite, index) => {
    const material = smokeMaterials[index]!;
    const base = sprite.position.clone();
    const baseScale = sprite.scale.x;
    const layerInfluence = 1 - Math.min(base.y / 1.55, 1) * 0.22;
    const maxOpacity = (0.42 + noise(index * 13 + 5) * 0.3) * layerInfluence;
    const lift = 0.08 + noise(index * 17 + 6) * 0.18;
    const driftX = (noise(index * 19 + 7) - 0.5) * 0.16;
    const driftZ = (noise(index * 23 + 8) - 0.5) * 0.14;
    const phase = noise(index * 29 + 9) * Math.PI * 2;
    const cycle = { value: 0 };
    const handle = registry.create(`character_status_curse_plume_${index}`);
    handles.add(handle);
    handle.timeline.to(
      cycle,
      {
        value: 1,
        duration: 2.1 + noise(index * 31 + 10) * 0.8,
        ease: 'none',
        repeat: -1,
        delay: index * 0.035,
        onUpdate: () => {
          const t = cycle.value;
          const fade = Math.pow(Math.sin(t * Math.PI), 0.72);
          const swirl = Math.sin(t * Math.PI * 2 + phase);
          material.opacity = maxOpacity * fade;
          sprite.position.set(
            base.x + driftX * t + swirl * 0.035,
            base.y + lift * t,
            base.z + driftZ * t + Math.cos(t * Math.PI * 2 + phase) * 0.03,
          );
          const scale = baseScale * (1 + t * 0.62);
          sprite.scale.set(scale, scale, 1);
        },
      },
      0,
    );
  });

  return { id: 'curse', playerId, rig, group, handles, finishing: false };
}

function createShellMaterial(effectId: Exclude<CharacterStatusEffectId, 'curse'>): THREE.Material {
  if (effectId === 'ice') {
    return new THREE.MeshStandardMaterial({
      color: 0xcceeff,
      emissive: 0x447799,
      emissiveIntensity: 0.25,
      transparent: true,
      opacity: 1,
      roughness: 0.25,
      metalness: 0.02,
      depthWrite: true,
    });
  }
  return createStoneSkinShader();
}

function createFragmentMaterial(
  effectId: Exclude<CharacterStatusEffectId, 'curse'>,
): THREE.Material {
  if (effectId === 'ice') {
    return new THREE.MeshStandardMaterial({
      color: 0xe9f8ff,
      emissive: 0x88ddff,
      emissiveIntensity: 0.28,
      transparent: true,
      opacity: 0.72,
      roughness: 0.18,
      depthWrite: false,
    });
  }

  return new THREE.MeshStandardMaterial({
    color: 0x6d665e,
    transparent: true,
    opacity: 0.9,
    roughness: 1,
  });
}

function addShellFragments(
  group: THREE.Group,
  effectId: Exclude<CharacterStatusEffectId, 'curse'>,
): void {
  const material = createFragmentMaterial(effectId);
  const geometry =
    effectId === 'ice'
      ? new THREE.TetrahedronGeometry(0.075, 0)
      : new THREE.DodecahedronGeometry(0.08, 0);
  for (let index = 0; index < 9; index += 1) {
    const angle = (index / 9) * Math.PI * 2;
    const mesh = new THREE.Mesh(geometry.clone(), material.clone());
    mesh.userData.characterStatusFragment = effectId;
    mesh.position.set(Math.cos(angle) * 0.34, 0.28 + (index % 4) * 0.22, Math.sin(angle) * 0.22);
    mesh.rotation.set(index * 0.8, angle, index * 0.37);
    mesh.scale.setScalar(index % 2 === 0 ? 1.15 : 0.82);
    mesh.renderOrder = STATUS_SHELL_RENDER_ORDER;
    group.add(mesh);
  }
  geometry.dispose();
  material.dispose();
}

function setGroupOpacity(group: THREE.Object3D, multiplier: number): void {
  group.traverse((child) => {
    const mesh = child as THREE.Mesh | THREE.Sprite;
    const material = mesh.material;
    if (material == null) return;
    const materials = Array.isArray(material) ? material : [material];
    for (const mat of materials) {
      const materialWithOpacity = mat as THREE.Material & {
        opacity?: number;
        userData: Record<string, unknown>;
      };
      const shaderMaterial = mat as THREE.ShaderMaterial & {
        uniforms?: Record<string, THREE.IUniform<unknown>>;
      };
      const opacityUniform = shaderMaterial.uniforms?.uOpacity;
      if (opacityUniform != null && typeof opacityUniform.value === 'number') {
        const base =
          typeof materialWithOpacity.userData.baseOpacity === 'number'
            ? materialWithOpacity.userData.baseOpacity
            : opacityUniform.value;
        materialWithOpacity.userData.baseOpacity = base;
        opacityUniform.value = base * multiplier;
        materialWithOpacity.transparent = true;
        continue;
      }
      if (typeof materialWithOpacity.opacity !== 'number') continue;
      const base =
        typeof materialWithOpacity.userData.baseOpacity === 'number'
          ? materialWithOpacity.userData.baseOpacity
          : materialWithOpacity.opacity;
      materialWithOpacity.userData.baseOpacity = base;
      materialWithOpacity.opacity = base * multiplier;
      materialWithOpacity.transparent = true;
    }
  });
}

function destroyRecord(record: EffectRecord): void {
  for (const handle of record.handles) handle.dispose();
  record.handles.clear();
  record.group.parent?.remove(record.group);
  disposeObject(record.group);
}

function disposeObject(object: THREE.Object3D): void {
  object.traverse((child) => {
    const mesh = child as THREE.Mesh | THREE.Sprite;
    if ('geometry' in mesh && mesh.geometry instanceof THREE.BufferGeometry) {
      mesh.geometry.dispose();
    }
    const material = mesh.material;
    if (material == null) return;
    const materials = Array.isArray(material) ? material : [material];
    for (const mat of materials) mat.dispose();
  });
  object.clear();
}
